import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Row, Col, Card, CardBody, Form, FormGroup, Label, Input,
  Button, Alert, Spinner
} from 'reactstrap';
import { RootState, AppDispatch } from '../../store';
import { createLead } from '../../slices/onboarding/thunk';
import { setOnboardingFlowId } from '../../slices/onboarding/reducer';
import { config } from '../../config/environment';
import { toast } from 'react-toastify';

const GettingStarted: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Redux state
  const { loading, error } = useSelector((state: RootState) => state.onboarding);

  // Local state
  const [checkingExistingFlow, setCheckingExistingFlow] = useState(false);
  const [existingFlowFound, setExistingFlowFound] = useState<any>(null);

  // Get initial values from URL parameters
  const getInitialValues = () => {
    const emailParam = searchParams.get('email');

    return {
      email: emailParam || '',
      organizationName: '',
      firstName: '',
      lastName: '',
    };
  };

  const validationSchema = Yup.object({
    email: Yup.string().email('Invalid email').required('Email is required'),
    organizationName: Yup.string().required('Organization name is required'),
    firstName: Yup.string().required('First name is required'),
    lastName: Yup.string().required('Last name is required'),
  });

  const formik = useFormik({
    initialValues: getInitialValues(),
    validationSchema,
    onSubmit: async (values) => {
      try {
        // First check if there's an existing active flow for this email
        const existingFlow = await checkForExistingFlow(values.email);
        if (existingFlow) {
          console.log('Found existing onboarding flow:', existingFlow.id);
          // Store the flow ID and redirect to appropriate step
          localStorage.setItem('onboardingFlowId', existingFlow.id);
          dispatch(setOnboardingFlowId(existingFlow.id));

          // Navigate to the appropriate page based on flow status
          redirectToFlowStep(existingFlow);
          return;
        }

        // No existing flow, proceed with normal lead creation
        await dispatch(createLead(values));

        // After lead creation, get the flow ID and redirect to verification
        const currentFlowId = localStorage.getItem('onboardingFlowId');
        if (currentFlowId) {
          toast.success(`Verification code sent to ${values.email}`);
          navigate(`/verify-email?flow=${currentFlowId}`);
        } else {
          toast.error('Failed to create onboarding session');
        }
      } catch (error) {
        console.error('Error creating lead:', error);
      }
    },
  });

  const checkForExistingFlow = async (email: string) => {
    try {
      setCheckingExistingFlow(true);

      const response = await fetch(config.api.activeFlowByEmail(email), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const flow = await response.json();
        setExistingFlowFound(flow);
        return flow;
      } else if (response.status === 404) {
        setExistingFlowFound(null);
        return null;
      } else {
        return null;
      }
    } catch (error) {
      return null;
    } finally {
      setCheckingExistingFlow(false);
    }
  };

  // Debounced email check for real-time feedback
  useEffect(() => {
    if (!formik.values.email || formik.values.email.length < 5) {
      setExistingFlowFound(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      if (formik.values.email.includes('@')) {
        await checkForExistingFlow(formik.values.email);
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [formik.values.email]);

  const redirectToFlowStep = (flow: any) => {
    const flowId = flow.id;

    switch (flow.status) {
      case 'INITIATED':
        if (!flow.emailVerifiedVia) {
          navigate(`/verify-email?flow=${flowId}`);
        } else {
          navigate(`/select-plans?flow=${flowId}`);
        }
        break;
      case 'EMAIL_VERIFIED':
        navigate(`/select-plans?flow=${flowId}`);
        break;
      case 'PAID':
        navigate(`/create-organization?flow=${flowId}`);
        break;
      case 'CONFIGURING_STRUCTURE':
        navigate(`/configure-structure?flow=${flowId}`);
        break;
      case 'COMPLETE':
        navigate(`/complete?flow=${flowId}`);
        break;
      default:
        navigate(`/getting-started?flow=${flowId}`);
    }
  };

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-12 col-md-10 col-lg-8 col-xl-6">
          <div className="onboarding-card-wrapper">
            <Card className="border-0">
              <CardBody className="p-4 p-md-5">
                <div className="text-center mb-4">
                  <div className="mb-3">
                    <div className="onboarding-icon">
                      <i className="fa-solid fa-rocket" />
                    </div>
                  </div>
                  <h4 className="mb-2 fw-bold">Get Started with Mentorgain</h4>
                  <p className="text-muted mb-0">
                    Join thousands of organizations building stronger mentorship programs
                  </p>
                </div>

                {error && <Alert color="danger">{error}</Alert>}

                {checkingExistingFlow && (
                  <Alert color="info" className="d-flex align-items-center">
                    <Spinner size="sm" className="me-2" />
                    Checking for existing onboarding process...
                  </Alert>
                )}

                {existingFlowFound && (
                  <Alert color="success" className="d-flex align-items-center justify-content-between">
                    <div className="d-flex align-items-center">
                      <i className="fa-solid fa-hand me-2" style={{ fontSize: '1.1rem' }} />
                      <div>
                        <strong>Welcome back!</strong> We found your existing onboarding process.
                        <br />
                        <small className="text-muted">Click "Resume Now" to continue where you left off.</small>
                      </div>
                    </div>
                    <Button
                      color="success"
                      size="sm"
                      onClick={() => {
                        localStorage.setItem('onboardingFlowId', existingFlowFound.id);
                        dispatch(setOnboardingFlowId(existingFlowFound.id));
                        redirectToFlowStep(existingFlowFound);
                      }}
                    >
                      Resume Now
                    </Button>
                  </Alert>
                )}

                <Form onSubmit={formik.handleSubmit}>
                  <Row>
                    <Col xs="12" md="6">
                      <FormGroup>
                        <Label for="firstName">First Name *</Label>
                        <Input
                          type="text"
                          id="firstName"
                          name="firstName"
                          placeholder="John"
                          value={formik.values.firstName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.firstName && !!formik.errors.firstName}
                        />
                        {formik.touched.firstName && formik.errors.firstName && (
                          <div className="invalid-feedback">{formik.errors.firstName}</div>
                        )}
                      </FormGroup>
                    </Col>
                    <Col xs="12" md="6">
                      <FormGroup>
                        <Label for="lastName">Last Name *</Label>
                        <Input
                          type="text"
                          id="lastName"
                          name="lastName"
                          placeholder="Doe"
                          value={formik.values.lastName}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={formik.touched.lastName && !!formik.errors.lastName}
                        />
                        {formik.touched.lastName && formik.errors.lastName && (
                          <div className="invalid-feedback">{formik.errors.lastName}</div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  <FormGroup>
                    <Label for="email">Work Email *</Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="john.doe@company.com"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.email && !!formik.errors.email}
                    />
                    {formik.touched.email && formik.errors.email && (
                      <div className="invalid-feedback">{formik.errors.email}</div>
                    )}
                    <small className="text-muted mt-1 d-block">
                      Please use your business email (personal email providers not allowed)
                    </small>
                  </FormGroup>

                  <FormGroup>
                    <Label for="organizationName">Organization Name *</Label>
                    <Input
                      type="text"
                      id="organizationName"
                      name="organizationName"
                      placeholder="Acme Corp"
                      value={formik.values.organizationName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      invalid={formik.touched.organizationName && !!formik.errors.organizationName}
                    />
                    {formik.touched.organizationName && formik.errors.organizationName && (
                      <div className="invalid-feedback">{formik.errors.organizationName}</div>
                    )}
                  </FormGroup>

                  <Button
                    type="submit"
                    color="primary"
                    size="lg"
                    block
                    disabled={loading || checkingExistingFlow}
                    className="mt-4"
                  >
                    {(loading || checkingExistingFlow) ? <Spinner size="sm" className="me-2" /> : null}
                    {checkingExistingFlow ? 'Checking...' : 'Continue'}
                  </Button>
                </Form>

                <div className="text-center mt-4 pt-3 border-top">
                  <p className="text-muted small mb-0">
                    By continuing, you agree to our{' '}
                    <a href="/terms" className="text-decoration-none">Terms of Service</a>
                    {' '}and{' '}
                    <a href="/privacy" className="text-decoration-none">Privacy Policy</a>
                  </p>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GettingStarted;
